import { api } from './api';

export interface TemplateItem {
  id: number;
  sectionKey: string;
  fieldKey: string;
  valuesJson: string;
}

export interface Template {
  id: number;
  name: string;
  description: string;
  scope: 'system' | 'user';
  category: string | null;
  createdByUserId: number | null;
  sourceReportId: number | null;
  usageCount: number;
  lastUsedAt: string | null;
  items: TemplateItem[];
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

/**
 * Zentrale Kategorienliste, Frontend-Pendant zu TEMPLATE_CATEGORIES in
 * api/src/report-templates/report-template-categories.ts — muss damit synchron bleiben.
 */
export const TEMPLATE_CATEGORIES: Array<{ value: string; label: string }> = [
  { value: 'digitalisierung', label: 'Digitalisierung' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'vertrieb', label: 'Vertrieb' },
  { value: 'organisation', label: 'Organisation' },
  { value: 'personal', label: 'Personal' },
  { value: 'finanzen', label: 'Finanzen' },
  { value: 'sonstiges', label: 'Sonstiges' },
];

export function categoryLabel(value: string | null): string {
  if (!value) return 'Ohne Kategorie';
  return TEMPLATE_CATEGORIES.find((c) => c.value === value)?.label ?? value;
}

/** Auswahl für applyToReport: welche Bullet-Positionen eines Items übernommen werden
 *  sollen. indices fehlt => alle Bullets dieses Items. */
export interface ApplySelection {
  itemId: number;
  indices?: number[];
}

export interface TemplateItemInput {
  sectionKey: string;
  fieldKey: string;
  values: unknown[];
}

export interface AdminReport {
  id: number;
  berichtstitel: string;
  customer: { id: number; companyName: string };
  user: { id: number; email: string; companyName: string | null; contactPerson: string };
  status: string;
  fortschritt: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface AdminReportFull extends AdminReport {
  sections: Array<{
    id: number;
    sectionKey: string;
    title: string;
    content: string | null;
    aiDraft: string | null;
    finalText: string | null;
    progress: number;
    completed: boolean;
  }>;
}

export const reportTemplatesApi = {
  list: () => api.get<Template[]>('/api/report-templates'),

  get: (id: number) => api.get<Template>(`/api/report-templates/${id}`),

  create: (data: {
    name: string;
    description?: string;
    scope?: 'system' | 'user';
    category?: string | null;
    items: TemplateItemInput[];
  }) => api.post<Template>('/api/report-templates', data),

  update: (
    id: number,
    data: { name?: string; description?: string; category?: string | null; items?: TemplateItemInput[] },
  ) => api.patch<Template>(`/api/report-templates/${id}`, data),

  remove: (id: number) => api.delete(`/api/report-templates/${id}`),

  listDeleted: () => api.get<Template[]>('/api/report-templates/deleted'),

  restore: (id: number) => api.patch<Template>(`/api/report-templates/${id}/restore`, {}),

  duplicate: (id: number) => api.post<Template>(`/api/report-templates/${id}/duplicate`, {}),

  createFromReport: (
    reportId: number,
    data: { name: string; description?: string; scope?: 'system' | 'user'; category?: string | null },
  ) => api.post<Template>(`/api/report-templates/from-report/${reportId}`, data),

  apply: (
    templateId: number,
    reportId: number,
    strategy: 'replace' | 'merge' = 'merge',
    items?: ApplySelection[],
    aiInstructions?: string,
  ) =>
    api.post<{ ok: boolean; skippedFields: string[] }>(
      `/api/report-templates/${templateId}/apply/${reportId}`,
      { strategy, items, aiInstructions },
    ),

  promote: (id: number) => api.post<Template>(`/api/report-templates/${id}/promote`),
};

export const adminReportsApi = {
  list: (filters: { search?: string; userId?: number; status?: string } = {}) => {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.userId) params.set('userId', String(filters.userId));
    if (filters.status) params.set('status', filters.status);
    const qs = params.toString();
    return api.get<AdminReport[]>(`/api/auth/admin/reports${qs ? '?' + qs : ''}`);
  },

  getFull: (id: number) => api.get<AdminReportFull>(`/api/auth/admin/reports/${id}/full`),
};

export interface PromptExample {
  id: number;
  cardId: string;
  sectionTitle: string;
  wzCode: string | null;
  wzFull: string | null;
  branche: string | null;
  customerName: string | null;
  exampleText: string;
  sourceReportId: number | null;
  isActive: boolean;
  createdAt: string;
}

export const promptExamplesApi = {
  saveFromReport: (reportId: number) =>
    api.post<{ saved: number }>(`/api/prompt-examples/from-report/${reportId}`, {}),
  listAll: () =>
    api.get<PromptExample[]>('/api/prompt-examples'),
  savedReportIds: () =>
    api.get<number[]>('/api/prompt-examples/saved-report-ids'),
  toggle: (id: number, isActive: boolean) =>
    api.patch<{ ok: boolean }>(`/api/prompt-examples/${id}/toggle`, { isActive }),
  delete: (id: number) =>
    api.delete<{ ok: boolean }>(`/api/prompt-examples/${id}`),
};

/** Helper: parse valuesJson sicher zu string[]. */
export function parseValues(item: { valuesJson: string }): unknown[] {
  try {
    const v = JSON.parse(item.valuesJson);
    // Objekte NICHT herausfiltern: die Listen eines Berichts sind Objekte, und
    // wer sie hier verwirft, leert die Vorlage beim naechsten Speichern.
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}
